import React from "react";
import { Box, Button, Typography } from "@mui/material";

type SelectedTeethMap = Record<string, string>;

type ToothSelectionGridProps = {
  selectedTeeth: SelectedTeethMap;
  handleToothSelect: (toothKey: string) => void;
};

const upperRight = ["UR8", "UR7", "UR6", "UR5", "UR4", "UR3", "UR2", "UR1"];
const upperLeft = ["UL1", "UL2", "UL3", "UL4", "UL5", "UL6", "UL7", "UL8"];
const lowerRight = ["LR8", "LR7", "LR6", "LR5", "LR4", "LR3", "LR2", "LR1"];
const lowerLeft = ["LL1", "LL2", "LL3", "LL4", "LL5", "LL6", "LL7", "LL8"];

const ToothSelectionGrid = ({
  selectedTeeth = {},
  handleToothSelect,
}: ToothSelectionGridProps) => {
  const isSelected = (tooth: string) =>
    selectedTeeth?.[`tooth_${tooth.toLowerCase()}`] === "Yes";

  const renderTooth = (tooth: string, arch: "upper" | "lower") => {
    const selected = isSelected(tooth);

    return (
      <Button
        key={tooth}
        variant="outlined"
        onClick={() => handleToothSelect(`tooth_${tooth.toLowerCase()}`)}
        sx={{
          minWidth: 62,
          width: 62,
          height: arch === "upper" ? 88 : 88,
          borderRadius: arch === "upper" ? "18px 18px 10px 10px" : "10px 10px 18px 18px",
          border: selected ? "2px solid #7c3aed" : "1.5px solid #d1d5db",
          background: selected
            ? "linear-gradient(180deg, #ede9fe 0%, #ddd6fe 100%)"
            : "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
          color: selected ? "#5b21b6" : "#334155",
          fontSize: "0.82rem",
          fontWeight: 700,
          textTransform: "none",
          boxShadow: selected
            ? "0 8px 18px rgba(124, 58, 237, 0.18)"
            : "0 4px 10px rgba(15, 23, 42, 0.06)",
          transition: "all 0.18s ease",
          "&:hover": {
            borderColor: selected ? "#6d28d9" : "#94a3b8",
            background: selected
              ? "linear-gradient(180deg, #e9e3ff 0%, #d8ccff 100%)"
              : "linear-gradient(180deg, #ffffff 0%, #f1f5f9 100%)",
            transform: "translateY(-2px)",
          },
        }}
      >
        {tooth}
      </Button>
    );
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 1200,
        mx: "auto",
        p: 3,
        borderRadius: 4,
        background:
          "linear-gradient(180deg, #ffffff 0%, #faf7ff 100%)",
        border: "1px solid #e9d5ff",
        boxShadow: "0 12px 30px rgba(91, 33, 182, 0.08)",
      }}
    >
      <Typography
        variant="h5"
        sx={{
          textAlign: "center",
          fontWeight: 700,
          color: "#312e81",
          mb: 0.5,
        }}
      >
        Tooth Selection
      </Typography>

      <Typography
        variant="body2"
        sx={{
          textAlign: "center",
          color: "#6b7280",
          mb: 3,
        }}
      >
        Select the affected teeth from the upper and lower arches
      </Typography>

      {/* Upper arch */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="subtitle1"
          sx={{
            textAlign: "center",
            fontWeight: 700,
            color: "#4338ca",
            mb: 2,
          }}
        >
          Upper Arch
        </Typography>

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-end",
            gap: 1.2,
            flexWrap: "nowrap",
            overflowX: "auto",
            pb: 1,
          }}
        >
          {upperRight.map((tooth, index) => (
            <Box
              key={tooth}
              sx={{
                mt: `${index * 4}px`,
              }}
            >
              {renderTooth(tooth, "upper")}
            </Box>
          ))}

          <Box sx={{ width: 40 }} />

          {upperLeft.map((tooth, index) => (
            <Box
              key={tooth}
              sx={{
                mt: `${(7 - index) * 4}px`,
              }}
            >
              {renderTooth(tooth, "upper")}
            </Box>
          ))}
        </Box>
      </Box>

      {/* Divider curve zone */}
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          mb: 3,
        }}
      >
        <Box
          sx={{
            width: "68%",
            height: 22,
            borderTop: "3px dashed #c4b5fd",
            borderRadius: "50%",
            opacity: 0.9,
          }}
        />
      </Box>

      {/* Lower arch */}
      <Box>
        <Typography
          variant="subtitle1"
          sx={{
            textAlign: "center",
            fontWeight: 700,
            color: "#4338ca",
            mb: 2,
          }}
        >
          Lower Arch
        </Typography>

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            gap: 1.2,
            flexWrap: "nowrap",
            overflowX: "auto",
            pb: 1,
          }}
        >
          {lowerRight.map((tooth, index) => (
            <Box
              key={tooth}
              sx={{
                mb: `${index * 4}px`,
              }}
            >
              {renderTooth(tooth, "lower")}
            </Box>
          ))}

          <Box sx={{ width: 40 }} />

          {lowerLeft.map((tooth, index) => (
            <Box
              key={tooth}
              sx={{
                mb: `${(7 - index) * 4}px`,
              }}
            >
              {renderTooth(tooth, "lower")}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default ToothSelectionGrid;